<template>
  <div>
    <banner-component title="Développement de logiciel et d'application web sur mesure" :lead="lead" :breadcrumbs="breadcrumbs" ruled>
      <router-link class="btn btn--primary" to="/contact">Demander un devis</router-link>
      <router-link class="btn btn--ghost" to="/gestionnaire-citron">Voir un logiciel que j'ai créé</router-link>
    </banner-component>

    <section class="band" aria-labelledby="services-title">
      <div class="container">
        <h2 id="services-title">Ce que je fais pour votre logiciel</h2>
        <ul class="services">
          <li>
            <h3>Maintenance et correction de bugs</h3>
            <p>Votre logiciel présente des erreurs ou des dysfonctionnements ? Je les corrige pour retrouver un outil fiable.</p>
          </li>
          <li>
            <h3>Amélioration et optimisation</h3>
            <p>Nouvelles fonctions, performances, interface modernisée : je fais évoluer votre solution selon vos besoins.</p>
          </li>
          <li>
            <h3>Création d'une application web (SaaS)</h3>
            <p>De l'analyse du besoin à la mise en production : conception, développement, hébergement et suivi.</p>
          </li>
          <li>
            <h3>Migration et évolution</h3>
            <p>Passage à une nouvelle technologie ou adaptation aux nouveaux standards, comme la facturation électronique.</p>
          </li>
          <li>
            <h3>Sécurité et fiabilité</h3>
            <p>Vos données et celles de vos utilisateurs sont précieuses : je suis les bonnes pratiques pour un logiciel robuste.</p>
          </li>
          <li>
            <h3>Web, API, desktop, mobile</h3>
            <p>Quelle que soit votre pile technique, je m'y adapte ou je vous conseille sur le meilleur choix.</p>
          </li>
        </ul>
      </div>
    </section>

    <section class="band band--white" aria-labelledby="proof-title">
      <div class="container columns-2">
        <div>
          <h2 id="proof-title">Un logiciel que je développe et que j'exploite</h2>
          <p>
            Le <router-link to="/gestionnaire-citron">Gestionnaire Citron</router-link> est une application SaaS de devis, de factures et de facturation électronique.
            Je l'ai conçue, je la fais évoluer et je réponds à ses utilisateurs : je sais ce que coûte un bug en production.
          </p>
          <p>
            Vous avez une obligation à intégrer, comme le format <router-link to="/blog/factur-x-format-facture-electronique">Factur-X</router-link> pour la facturation électronique ?
            C'est un sujet que je connais de l'intérieur.
          </p>
        </div>
        <div>
          <h3>Technologies que je pratique</h3>
          <ul class="stack">
            <li>Vue 3, JavaScript, TypeScript</li>
            <li>Node.js, Meteor, MongoDB</li>
            <li>Java, PHP, Python</li>
            <li>Docker, GitLab CI, Jenkins</li>
            <li>API REST, RabbitMQ</li>
          </ul>
        </div>
      </div>
    </section>

    <section class="band" aria-labelledby="method-title">
      <div class="container container--narrow">
        <h2 id="method-title">Un accompagnement de A à Z</h2>
        <p class="lead">Je ne me contente pas de coder : je vous conseille et je vous guide tout au long du projet, de l'analyse de vos besoins à la mise en production, avec un suivi personnalisé.</p>
      </div>
    </section>

    <advices-component/>

    <section class="band" aria-labelledby="faq-title">
      <div class="container container--narrow">
        <h2 id="faq-title">Questions fréquentes</h2>
        <faq-section :items="faq"/>
      </div>
    </section>

    <call-to-action-component variant="services"/>
  </div>
</template>

<script>
  import pages from "@/content/pages";
  import {inline} from "@/seo/inline";
  import {useSeo} from "@/seo/useSeo";
  import {organization, person, webPage, breadcrumb, faqPage, service, absoluteUrl} from "@/seo/schema";
  import BannerComponent from "@/components/BannerComponent.vue";
  import FaqSection from "@/components/FaqSection.vue";
  import AdvicesComponent from "@/components/AdvicesComponent.vue";
  import CallToActionComponent from "@/components/CallToActionComponent.vue";

  const PATH = '/logiciel';

  const BREADCRUMBS = [
    {name: 'Accueil', path: '/'},
    {name: 'Logiciel sur mesure', path: PATH}
  ];

  const FAQ = [
    {
      q: 'Pouvez-vous reprendre un logiciel développé par quelqu\'un d\'autre ?',
      a: 'Oui. Je commence par analyser l\'existant et vos besoins, puis je vous propose les corrections ou les évolutions les plus utiles, avant de chiffrer.'
    },
    {
      q: 'Avec quelles technologies travaillez-vous ?',
      a: 'Mon quotidien, c\'est Vue 3, Node.js et MongoDB, avec Docker et l\'intégration continue. J\'ai aussi travaillé avec Java, PHP, Python et Angular. Pour une autre technologie, nous en parlons lors de l\'échange.'
    },
    {
      q: 'Comment est établi le devis ?',
      a: 'Après un échange sur votre besoin, je vous envoie un devis personnalisé. Vous le validez avant que le travail ne commence.'
    },
    {
      q: 'Assurez-vous la maintenance après la livraison ?',
      a: 'Oui : correction de bugs, évolutions et mises à jour de sécurité font partie de ce que je propose.'
    }
  ];

  export default {
    name: 'SoftwareView',
    components: {BannerComponent, FaqSection, AdvicesComponent, CallToActionComponent},
    setup() {
      const page = pages.find(entry => entry.path === PATH);
      useSeo({
        path: PATH,
        image: '/img/og/logiciel.jpg',
        graph: [
          organization(),
          person(),
          webPage(PATH, page.title, page.description, {breadcrumb: {'@id': `${absoluteUrl(PATH)}#breadcrumb`}}),
          {...breadcrumb(BREADCRUMBS), '@id': `${absoluteUrl(PATH)}#breadcrumb`},
          service('Développement de logiciel et d\'application web sur mesure', page.description, PATH, 'Développement logiciel'),
          faqPage(FAQ)
        ]
      });
    },
    data: function () {
      return {
        faq: FAQ,
        breadcrumbs: BREADCRUMBS,
        lead: inline('Un logiciel qui ne répond plus à vos besoins, des bugs, une nouvelle fonction à ajouter ? Je crée, répare et fais évoluer des **logiciels et des applications web** pour les entreprises, depuis Toulouse.'),
      }
    }
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

.services
  list-style none
  margin 32px 0 0
  padding 0
  display grid
  gap 28px
  li
    margin 0
  h3
    margin 0 0 8px
    padding-top 14px
    border-top 2px solid INK
    font-size 1.3rem
  p
    margin 0
    color INK_SOFT

.columns-2
  display grid
  gap 32px
  align-items start
  h2
    margin-top 0
  p
    max-width 60ch

.stack
  margin 12px 0 0
  padding 0
  list-style none
  border-top 2px solid INK
  li
    margin 0
    padding 10px 0
    border-bottom 1px solid RULE

@media (min-width: DESKTOP_SIZE)
  .services
    grid-template-columns repeat(3, 1fr)
  .columns-2
    grid-template-columns 1.4fr 1fr
    gap 64px
</style>
